import produce from "immer"
import {
    CcRequirement,
    CcRequirementState,
    CcRequirementHistoryCreate,
} from "server/models"
import { InferValueTypes } from "client/utils"
import { ASC, IDLE } from "const"
import * as types from "./types"
import * as actions from "./actions"

type ActionTypes = ReturnType<InferValueTypes<typeof actions>>

export const initCcRequirement: CcRequirement = {
    id: null,
    projectId: null,
    fileId: null,
    file: null,
    uniqueId: "",
    name: "",
    description: "",
    creator: null,
    typeId: null,
    priorityId: null,
    statusId: null,
    reviewMethods: null,
    smartCompliance: null,

    reviewMethodsParsed: {},
    smartComplianceParsed: {},
    phaseLinks: [],
}

export const initCcRequirementState: CcRequirementState = {
    requirementId: null,
    requirementRevisionId: null,
    levelId: null,
    verifierId: null,
    settings: null,
}

export const initCcRequirementHistory: CcRequirementHistoryCreate = {
    requirementId: null,
    requirementRevisionId: null,
    parentId: null,
    prevLevelId: null,
    prevUserId: null,
    nextLevelId: null,
    nextUserId: null,
    responseId: null,
    remark: null,
    prevStatus: null,
    nextStatus: null,
    workflow: null,
    systemMessage: true,
    editorId: null,
}

const initCcRequirementStoreState: types.CcRequirementStoreState = {
    // assign requirements to lifecycle phase
    selected: null,
    onSelect: null,

    // list
    baseRoute: null,
    baseReviewRoute: null,
    tableClass: "requirement-review-table",
    showColumnSettings: false,
    dictionariesLoaded: false,

    requirementList: {},
    requirementParentChild: [],
    requirementGroupedIds: [],
    requirementIds: [],

    revisionList: {},
    revisionActiveList: {},

    requirementStateList: {},
    requirementHistoryList: {},
    tempAnswerList: {},

    sortColumn: "name",
    sortOrder: ASC,
    filters: {
        search: null,
    },

    // edit
    requirementEdit: initCcRequirement,
    lifecyclesEdit: [],
    lifecyclePhaseListEdit: {},

    reloadEdit: true,

    assigned: {
        parents: {},
        children: {},
    },
    notAssigned: {
        requirementIds: [],
        sortColumn: "name",
        sortOrder: ASC,
        filters: {
            search: null,
        },
        selected: [],
    },

    versionList: {},

    // review
    selectedRequirementIds: [],
    visibleRequirementIds: [],

    reviewListLoadState: IDLE,

    // popup menu
    active: null,
    menuShow: false,
    menuTarget: null,

    // view history
    requirementDetails: initCcRequirement,
    requirementState: initCcRequirementState,
    requirementHistory: [],
    tempAnswers: {},
    tempAnswerVersions: {},
    tempAnswerHistory: null,
    reloadAnswer: true,
}

const ccRequirementReducer = produce((
    draft: types.CcRequirementStoreState = initCcRequirementStoreState,
    action: ActionTypes = null,
): types.CcRequirementStoreState => {
    switch (action.type) {
        // assign requirements to lifecycle phase
        case types.CC_REQUIREMENT_SELECTED:
            draft.selected = action.selected
            return draft

        case types.CC_REQUIREMENT_ON_SELECT:
            draft.onSelect = action.onSelect
            return draft

        // list
        case types.CC_REQUIREMENT_BASE_ROUTE:
            draft.baseRoute = action.baseRoute
            return draft

        case types.CC_REQUIREMENT_BASE_REVIEW_ROUTE:
            draft.baseReviewRoute = action.baseRoute
            return draft

        case types.CC_REQUIREMENT_COLUMN_SETTINGS:
            draft.showColumnSettings = action.show
            return draft

        case types.CC_REQUIREMENT_DICTIONARIES_LOADED:
            draft.dictionariesLoaded = action.loaded
            return draft

        case types.CC_REQUIREMENT_LIST:
            draft.requirementList = action.requirementList
            return draft

        case types.CC_REQUIREMENT_PARENT_CHILD:
            draft.requirementParentChild = action.parentChild
            return draft

        case types.CC_REQUIREMENT_NESTED:
            draft.requirementGroupedIds = action.requirementGroupedIds
            return draft

        case types.CC_REQUIREMENT_IDS:
            draft.requirementIds = action.requirementIds
            return draft

        case types.CC_REQUIREMENT_REVISION_LIST:
            draft.revisionList = {
                ...draft.revisionList,
                [action.requirementId]: action.revisions,
            }
            return draft

        case types.CC_REQUIREMENT_REVISION_ACTIVE_LIST:
            draft.revisionActiveList = {
                ...draft.revisionActiveList,
                [action.requirementId]: action.revision,
            }
            return draft

        case types.CC_REQUIREMENT_STATE_LIST:
            draft.requirementStateList = {
                ...draft.requirementStateList,
                [action.requirementId]: action.state,
            }
            return draft

        case types.CC_REQUIREMENT_HISTORY_LIST:
            draft.requirementHistoryList = {
                ...draft.requirementHistoryList,
                [action.requirementId]: action.historyList,
            }
            return draft

        case types.CC_REQUIREMENT_TEMP_ANSWER_LIST:
            draft.tempAnswerList = {
                ...draft.tempAnswerList,
                [action.requirementId]: action.tempAnswers,
            }
            return draft

        case types.CC_REQUIREMENT_SORT:
            draft.sortColumn = action.sortColumn
            draft.sortOrder = action.sortOrder
            return draft

        case types.CC_REQUIREMENT_FILTERS:
            draft.filters = action.filters
            return draft

        // edit
        case types.CC_REQUIREMENT_EDIT:
            draft.requirementEdit = action.requirement
            return draft

        case types.CC_REQUIREMENT_LIFECYCLES_EDIT:
            draft.lifecyclesEdit = action.lifecycles
            return draft

        case types.CC_REQUIREMENT_LIFECYCLE_PHASE_LIST_EDIT:
            draft.lifecyclePhaseListEdit = action.phaseList
            return draft

        case types.CC_REQUIREMENT_RELOAD_EDIT:
            draft.reloadEdit = action.value
            return draft

        case types.CC_REQUIREMENT_ASSIGNED_PARENTS:
            draft.assigned.parents = {
                ...draft.assigned.parents,
                [action.requirementId]: action.parentIds,
            }
            return draft

        case types.CC_REQUIREMENT_ASSIGNED_CHILDREN:
            draft.assigned.children = {
                ...draft.assigned.children,
                [action.requirementId]: action.childIds,
            }
            return draft

        case types.CC_REQUIREMENT_NOT_ASSIGNED_IDS:
            draft.notAssigned.requirementIds = action.requirementIds
            return draft

        case types.CC_REQUIREMENT_NOT_ASSIGNED_SORT:
            draft.notAssigned.sortColumn = action.sortColumn
            draft.notAssigned.sortOrder = action.sortOrder
            return draft

        case types.CC_REQUIREMENT_NOT_ASSIGNED_FILTERS:
            draft.notAssigned.filters = action.filters
            return draft

        case types.CC_REQUIREMENT_NOT_ASSIGNED_SELECTED:
            draft.notAssigned.selected = action.selected
            return draft

        case types.CC_REQUIREMENT_VERSION_LIST:
            draft.versionList = {
                ...draft.versionList,
                [action.requirementId]: {
                    versionIds: action.versionIds,
                    versionList: action.versionList,
                },
            }
            return draft

        // review
        case types.CC_REQUIREMENT_SELECTED_REQUIREMENT_IDS:
            draft.selectedRequirementIds = action.selected
            return draft

        case types.CC_REQUIREMENT_VISIBLE_REQUIREMENT_IDS:
            draft.visibleRequirementIds = action.visible
            return draft

        case types.CC_REQUIREMENT_REVIEW_LIST_LOAD_STATE:
            draft.reviewListLoadState = action.state
            return draft

        // popup menu
        case types.CC_REQUIREMENT_ACTIVE:
            draft.active = action.active
            return draft

        case types.CC_REQUIREMENT_POPUP_MENU_SHOW:
            draft.menuShow = action.show
            return draft

        case types.CC_REQUIREMENT_POPUP_MENU_TARGET:
            draft.menuTarget = action.target
            return draft

        // view history
        case types.CC_REQUIREMENT_REQUIREMENT_DETAILS:
            draft.requirementDetails = action.details
            return draft

        case types.CC_REQUIREMENT_REQUIREMENT_STATE:
            draft.requirementState = action.state
            return draft

        case types.CC_REQUIREMENT_REQUIREMENT_HISTORY:
            draft.requirementHistory = action.history
            return draft

        case types.CC_REQUIREMENT_TEMP_ANSWERS:
            draft.tempAnswers = action.answers
            return draft

        case types.CC_REQUIREMENT_TEMP_ANSWER_VERSIONS:
            draft.tempAnswerVersions = action.versions
            return draft

        case types.CC_REQUIREMENT_TEMP_ANSWER_HISTORY:
            draft.tempAnswerHistory = action.history
            return draft

        case types.CC_REQUIREMENT_RELOAD_ANSWER:
            draft.reloadAnswer = action.value
            return draft

        default:
            return draft
    }
})
export default ccRequirementReducer
